/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { Selection } from 'src/modules/labeling/types/Selection';
import { getDefaultState, INVALID_INDEX, LabelingAction, labelingReducer, LabelingState } from 'src/modules/labeling/utils/labelingReducer';

type TokenSelectionStoreConfig = { disabled: boolean; hideHoverBrackets: boolean };

export class TokenSelectionStore {
    @observable public config: TokenSelectionStoreConfig;

    @observable private state: LabelingState = getDefaultState(false);

    constructor(config: TokenSelectionStoreConfig) {
        this.config = config;
    }

    @computed
    public get selection(): Selection {
        return [this.state.labelStart, this.state.labelEnd];
    }

    @computed
    public get hoverSelection(): Selection {
        return this.config.hideHoverBrackets ? [INVALID_INDEX, INVALID_INDEX] : [this.state.hoverStart, this.state.hoverEnd];
    }

    @computed
    public get isDragging(): boolean {
        return this.state.dragging;
    }

    /**
     * Sets the configuration of the token selection store.
     */
    @action
    public setConfig(config: TokenSelectionStoreConfig) {
        this.config.disabled = config.disabled;
        this.config.hideHoverBrackets = config.hideHoverBrackets;
        this._dispatch({ type: 'SetDisabled', params: { disabled: config.disabled } });
    }

    /**
     * Labels a token.
     */
    @action public label(index: number, isKeyboardInteraction?: boolean) {
        this._dispatch({ type: 'Label', params: { index, isKeyboardInteraction } });
    }

    /**
     * Hovers a token.
     */
    @action public hover(index: number) {
        this._dispatch({ type: 'Hover', params: { index } });
    }

    /**
     * * Label left, commonly used with keyboard.
     */
    @action public labelLeft(index: number) {
        this._dispatch({ type: 'LabelLeft', params: { index } });
    }

    /**
     * Label right, commonly used with keyboard.
     */
    @action public labelRight(index: number) {
        this._dispatch({ type: 'LabelRight', params: { index } });
    }

    /**
     * Remove selection states.
     */
    @action public cancel() {
        this._dispatch({ type: 'Cancel' });
    }

    /**
     * Remove hover states.
     */
    @action public unHover() {
        this._dispatch({ type: 'UnHover' });
    }

    /**
     * Changes dragging to false.
     */
    @action public stopDragging() {
        this._dispatch({ type: 'StopDragging' });
    }

    /**
     * Selects all tokens.
     */
    @action public selectAll(lastIndex: number) {
        this._dispatch({ type: 'SelectAll', params: { lastIndex } });
    }

    /**
     * Private dispatch function.
     */
    @action private _dispatch(labelingAction: LabelingAction) {
        this.state = labelingReducer(this.state, labelingAction);
    }
}
